<template>
  <header class="admin-header">
    <div class="header-left">
      <!-- Mobile Menu Button -->
      <button
        v-if="isMobile"
        class="mobile-menu-btn"
        @click="toggleMobileMenu"
        :class="{ active: isMobileMenuOpen }"
      >
        <i class="bi" :class="isMobileMenuOpen ? 'bi-x' : 'bi-list'"></i>
      </button>
      <Logo class="admin-logo-left" />
    </div>
    
    <div class="header-right">
      <div class="admin-user">
        <div class="user-info">
          <span class="user-name">{{ currentUser.name }}</span>
          <span class="user-role">{{ currentUser.role }}</span>
        </div>
        <div class="user-avatar">
          <i class="bi bi-person-circle"></i>
        </div>
        <div class="user-menu">
          <button @click="toggleUserMenu" class="menu-toggle">
            <i class="bi bi-chevron-down"></i>
          </button>
          <div v-if="showUserMenu" class="dropdown-menu">
            <a href="#" @click="viewProfile">Profile</a>
            <a href="#" @click="logout">Logout</a>
          </div>
        </div>
      </div>
      <NotificationBell />
    </div>
  </header>
</template>

<script>
import Logo from './Logo.vue'
import NotificationBell from './NotificationBell.vue'
import { useAuth0 } from '@auth0/auth0-vue'
import { getPrimaryRole } from '@/utils/auth'

export default {
  name: 'AdminHeader',
  components: { Logo, NotificationBell },
  props: {
    mobileMenuOpen: {
      type: Boolean,
      default: false
    }
  },
  setup() {
    const { logout: auth0Logout, user } = useAuth0()
    return { auth0Logout, user }
  },
  data() {
    return {
      showUserMenu: false,
      isMobile: false
    }
  },
  computed: {
    currentUser() {
      return {
        name: this.user?.name || this.user?.email || 'Admin User',
        role: getPrimaryRole(this.user)
      }
    },
    isMobileMenuOpen() {
      return this.mobileMenuOpen
    }
  },
  methods: {
    toggleUserMenu() {
      this.showUserMenu = !this.showUserMenu
    },
    viewProfile() {
      this.showUserMenu = false
      // Navigate to profile page
    },
    logout() {
      this.showUserMenu = false
      this.auth0Logout({
        logoutParams: {
          returnTo: window.location.origin + '/login'
        }
      })
    },
    toggleMobileMenu() {
      this.$emit('mobile-menu-toggle', !this.isMobileMenuOpen)
    },
    checkIfMobile() {
      this.isMobile = window.innerWidth <= 768
      if (!this.isMobile && this.isMobileMenuOpen) {
        this.$emit('mobile-menu-toggle', false)
      }
    },
    handleResize() {
      this.checkIfMobile()
    }
  },
  mounted() {
    // Close user menu when clicking outside
    document.addEventListener('click', (e) => {
      if (!this.$el.contains(e.target)) {
        this.showUserMenu = false
      }
    })

    // Initialize mobile detection
    this.checkIfMobile()
    window.addEventListener('resize', this.handleResize)
  },
  beforeUnmount() {
    window.removeEventListener('resize', this.handleResize)
  }
}
</script>

<style scoped>
.admin-header {
  background-color: var(--primary);
  color: #fff;
  padding: 1rem 2rem;
  display: flex;
  justify-content: space-between;
  align-items: center;
  box-shadow: var(--shadow-md);
  /* border-bottom: 1px solid var(--border-light); */
  z-index: 10;
}

.header-left {
  display: flex;
  align-items: center;
  gap: 1rem;
  min-width: 0; /* allow the logo side to shrink instead of overflowing */
}

/* Mobile Menu Button */
.mobile-menu-btn {
  background: none;
  border: none;
  color: white;
  cursor: pointer;
  padding: 0.5rem;
  border-radius: 6px;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.2s ease;
  width: 40px;
  height: 40px;
}

.mobile-menu-btn:hover {
  background-color: rgba(255, 255, 255, 0.1);
}

.mobile-menu-btn.active {
  background-color: rgba(255, 255, 255, 0.15);
}

.mobile-menu-btn i {
  font-size: 1.25rem;
  transition: transform 0.2s ease;
}

.header-left .admin-title {
  font-size: 1.5rem;
  font-weight: 600;
  display: flex;
  align-items: center;
  gap: 0.5rem;
}

.header-left .admin-title i {
  color: var(--primary);
}

.admin-logo-left {
  filter: brightness(0) invert(1);
  display: flex;
  align-items: center;
  height: 60px;
}

.header-right {
  display: flex;
  align-items: center;
  gap: 1.5rem;
  color: #fff;
  flex-shrink: 0; /* keep the controls pinned — never push the bell off-screen */
}

.admin-logo-left {
  display: flex;
  align-items: center;
  height: 60px;
}

.admin-user {
  display: flex;
  align-items: center;
  gap: 1rem;
  position: relative;
}

.user-info {
  display: flex;
  flex-direction: column;
  align-items: flex-end;
}

.user-name {
  font-weight: 500;
  font-size: 0.875rem;
}

.user-role {
  font-size: 0.75rem;
  color: #e0f7ef;
}

.user-avatar i {
  font-size: 2rem;
  color: #e0f7ef;
}

.user-menu {
  position: relative;
}

.menu-toggle {
  background: none;
  border: none;
  color: #e0f7ef;
  cursor: pointer;
  padding: 0.25rem;
  border-radius: var(--radius-sm);
  transition: var(--transition);
}

.menu-toggle:hover {
  color: white;
  background-color: rgba(255, 255, 255, 0.1);
}

.dropdown-menu {
  position: absolute;
  top: 100%;
  right: 0;
  background: #fff;
  border-radius: var(--radius-sm);
  box-shadow: var(--shadow-xl);
  min-width: 150px;
  z-index: 1000;
  margin-top: 0.5rem;
  border: 1px solid var(--border-light);
}

.dropdown-menu a {
  display: block;
  padding: 0.75rem 1rem;
  color: var(--text-dark);
  text-decoration: none;
  font-size: 0.875rem;
  transition: var(--transition);
}

.dropdown-menu a:hover {
  background-color: var(--primary-light);
  color: var(--primary-dark);
}

.dropdown-menu a:first-child {
  border-radius: var(--radius-sm) var(--radius-sm) 0 0;
}

.dropdown-menu a:last-child {
  border-radius: 0 0 var(--radius-sm) var(--radius-sm);
}

/* Mobile Responsive Styles */
@media (max-width: 768px) {
  .admin-header {
    padding: 0.75rem 1rem;
  }

  .header-left {
    gap: 0.75rem;
  }

  .admin-logo-left {
    height: 40px;
  }

  /* Drop the "Ready Set Spray." wordmark on small screens — it renders ~240px
     wide and pushed the header controls past the right edge. Keep the spider.
     Scoped to the header so the login screen still shows the full logo. */
  .admin-logo-left :deep(.logo-svg) {
    display: none;
  }

  .admin-logo-left :deep(.logo-image) {
    height: 40px;
  }

  .header-right {
    gap: 1rem;
  }

  .user-info {
    display: none; /* Hide user info text on mobile to save space */
  }

  .user-avatar i {
    font-size: 1.5rem;
  }

  .admin-user {
    gap: 0.5rem;
  }

  .dropdown-menu {
    right: -0.5rem; /* Adjust positioning for mobile */
    min-width: 120px;
  }

  .dropdown-menu a {
    padding: 1rem 0.75rem; /* Larger touch targets */
    font-size: 1rem;
  }

  /* Ensure mobile menu button is properly sized */
  .mobile-menu-btn {
    width: 36px;
    height: 36px;
    padding: 0.375rem;
  }

  .mobile-menu-btn i {
    font-size: 1.125rem;
  }
}

@media (max-width: 480px) {
  .admin-header {
    padding: 0.5rem 0.75rem;
  }

  .admin-logo-left {
    height: 35px;
  }

  .header-right {
    gap: 0.75rem;
  }
}
</style>
